import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Trash2, 
  HelpCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const Questions = () => {
  const [questions, setQuestions] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [questionsPerPage] = useState(20)
  
  const { getQuestions, getCategories, deleteQuestion } = useFirebase()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [questionsData, categoriesData] = await Promise.all([
        getQuestions(),
        getCategories()
      ])
      setQuestions(questionsData)
      setCategories(categoriesData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load questions')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await deleteQuestion(id)
        setQuestions(questions.filter(q => q.id !== id))
        toast.success('Question deleted successfully')
      } catch (error) {
        console.error('Error deleting question:', error)
        toast.error('Failed to delete question')
      }
    }
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      const filters = {}
      if (searchTerm) filters.search = searchTerm
      if (selectedCategory) filters.category = selectedCategory
      
      const filteredQuestions = await getQuestions(filters)
      setQuestions(filteredQuestions)
      setCurrentPage(1)
    } catch (error) {
      console.error('Error searching questions:', error)
      toast.error('Failed to search questions')
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('')
    setCurrentPage(1)
    loadData()
  }

  // Pagination
  const indexOfLastQuestion = currentPage * questionsPerPage
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage
  const currentQuestions = questions.slice(indexOfFirstQuestion, indexOfLastQuestion)
  const totalPages = Math.ceil(questions.length / questionsPerPage)

  const getDifficultyBadge = (difficulty) => {
    const badges = {
      easy: 'badge-success',
      medium: 'badge-warning',
      hard: 'badge-danger'
    }
    return badges[difficulty] || 'badge-info'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questions</h1>
          <p className="text-sm text-gray-600">
            Manage exam questions for your nursing app
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link to="/questions/add" className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add New Question
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="search" className="form-label">
                Search Questions
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  id="search"
                  className="form-input pl-10"
                  placeholder="Search by question text..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                id="category"
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end space-x-2">
              <button onClick={handleSearch} className="btn-primary">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </button>
              <button onClick={clearFilters} className="btn-outline">
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Questions ({questions.length} total)
          </h3>
        </div>
        
        <div className="card-body">
          {currentQuestions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Category</th>
                    <th>Difficulty</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentQuestions.map((question) => {
                    const category = categories.find(c => c.id === question.category)
                    
                    return (
                      <tr key={question.id} className="hover:bg-gray-50">
                        <td>
                          <div className="max-w-xs">
                            <p className="font-medium text-gray-900 truncate">
                              {question.question?.substring(0, 100)}
                              {question.question?.length > 100 ? '...' : ''}
                            </p>
                            <p className="text-sm text-gray-500">
                              Correct: {question.options?.[question.correctAnswer]}
                            </p>
                          </div>
                        </td>
                        <td>
                          {category ? (
                            <span className="badge-info">{category.name}</span>
                          ) : (
                            <span className="badge-gray">Uncategorized</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${getDifficultyBadge(question.difficulty)}`}>
                            {question.difficulty || 'medium'}
                          </span>
                        </td>
                        <td>
                          {question.createdAt 
                            ? new Date(question.createdAt).toLocaleDateString()
                            : 'N/A'
                          }
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <Link
                              to={`/questions/edit/${question.id}`}
                              className="text-primary-600 hover:text-primary-900"
                              title="Edit question"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Link>
                            <button
                              onClick={() => handleDelete(question.id)}
                              className="text-danger-600 hover:text-danger-900"
                              title="Delete question"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No questions found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || selectedCategory 
                  ? 'Try adjusting your search criteria.'
                  : 'Get started by creating your first question.'
                }
              </p>
              <Link to="/questions/add" className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add First Question
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {indexOfFirstQuestion + 1} to {Math.min(indexOfLastQuestion, questions.length)} of {questions.length} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="btn-outline disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + Math.max(1, currentPage - 2)
              if (page > totalPages) return null
              
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              )
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="btn-outline disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Questions
